import React from 'react';

import { Layout } from '@components/Layout';
import { PrintableContainer } from '@components/Printable';
import { cellFactory, Table } from '@components/Table/Table';
import { bookingsStrings } from '@data/bookings';
import { usePreAlertLog } from '@hooks/queries/dashboard/usePreAlertLog';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { formatDateTime, toJST } from '@utils/types';
import { max } from 'lodash-es';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';

const useStyles = makeMuiStyles((theme, _common) => ({
  customInner: {
    maxWidth: '65rem',
  },
  background: {
    backgroundColor: theme.palette.background.paper,
  },
  dataNotExist: {
    color: theme.palette.grey[400],
    margin: '3rem auto',
  },
}));

const strings = createStrings({
  ja: {
    dataNotExist: '履歴なし',
    no: 'No',
    updatedAt: '更新時間',
    editor: '編集者',
    editedField: '変更内容',
    beforeValue: '変更前',
    afterValue: '変更後',
    detail: '変更理由',
  },
});

export const PrintPreAlertLog: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const location = useLocation();
  const preAlertId = parseInt(queryString.parse(location.search).id as string, 10);

  const preAlertLog = usePreAlertLog(preAlertId);

  const bodyColumns =
    preAlertLog.data?.map((item, index) => {
      return [
        cellFactory.create((index + 1).toString()),
        cellFactory.create(item.editor || '-'),
        cellFactory.create(formatDateTime(item.updated_at)),
        cellFactory.create(item.edited_field || '-'),
        cellFactory.create(item.before_value || '-'),
        cellFactory.create(item.after_value || '-'),
        cellFactory.create(item.detail || '-'),
      ];
    }) ?? [];

  let contents: React.ReactNode = null;
  if (preAlertLog.data && preAlertLog.data?.length > 0) {
    contents = (
      <Table>
        <Table.Head>
          <Table.Row
            columns={[
              cellFactory.create(strings.no || '-'),
              cellFactory.create(strings.editor || '-'),
              cellFactory.create(strings.updatedAt || '-'),
              cellFactory.create(strings.editedField || '-'),
              cellFactory.create(strings.beforeValue || '-'),
              cellFactory.create(strings.afterValue || '-'),
              cellFactory.create(strings.detail || '-'),
            ]}
          />
        </Table.Head>
        <Table.Body>
          {bodyColumns.map((columns) => {
            return <Table.Row columns={columns} />;
          })}
        </Table.Body>
      </Table>
    );
  } else if (preAlertLog.data && preAlertLog.data?.length === 0) {
    contents = (
      <>
        <Table>
          <Table.Head>
            <Table.Row
              columns={[
                cellFactory.create(strings.no || '-'),
                cellFactory.create(strings.editor || '-'),
                cellFactory.create(strings.updatedAt || '-'),
                cellFactory.create(strings.editedField || '-'),
                cellFactory.create(strings.beforeValue || '-'),
                cellFactory.create(strings.afterValue || '-'),
                cellFactory.create(strings.detail || '-'),
              ]}
            />
          </Table.Head>
        </Table>
        <p className={classes.dataNotExist}>{strings.dataNotExist}</p>
      </>
    );
  }

  const updatedAt = preAlertLog.data && max(preAlertLog.data.map((d) => d.updated_at));

  return (
    <Layout
      hideHeader
      hideFooter
      hideCookiePolicy
      loading={preAlertLog.isLoading}
      title={bookingsStrings.currentBookings}
      errors={[preAlertLog.error]}
      classes={{
        background: classes.background,
      }}
    >
      <PrintableContainer classes={{ inner: classes.customInner }}>
        {updatedAt && (
          <p className={classes.horizontalSpace1}>
            <span>Latest Update:</span>
            <span>{updatedAt ? toJST(new Date(updatedAt)) : '-'}</span>
          </p>
        )}
        {contents}
      </PrintableContainer>
    </Layout>
  );
};
